import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { patchProduction, type Production } from '@/api/studio-production'
import { AuthSessionExpiredError } from '@/api/authSession'
import { PROJECT_TYPES, type MemberRole } from '@/features/studio/lib/production-constants'
import { formatResolvedApiError, resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal, ProtoNumberInput, ProtoSelect, ProtoTextarea } from '@/features/studio/components/ui-kit'
import { toast } from '@/shared/lib/notify'

export function EditProductionModal({
  open,
  onClose,
  production,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  production: Production | null
  onSaved?: () => void
}) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [projectType, setProjectType] = useState<string>('comic_drama')
  const [aspectRatio, setAspectRatio] = useState('9:16')
  const [episodeTarget, setEpisodeTarget] = useState(1)
  const [defaultLocale, setDefaultLocale] = useState('zh-CN')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !production) return
    setError(null)
    setTitle(production.title ?? '')
    setDescription(production.description ?? '')
    setProjectType(production.project_type?.trim() || 'comic_drama')
    setAspectRatio(production.aspect_ratio?.trim() || '9:16')
    setEpisodeTarget(Math.max(1, production.episode_target ?? 1))
    setDefaultLocale(production.default_locale?.trim() || 'zh-CN')
  }, [open, production])

  async function handleSubmit() {
    if (!production) return
    const trimmed = title.trim()
    if (!trimmed) {
      setError(t('createModal.titleRequired'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await patchProduction(production.public_id, {
        title: trimmed,
        description: description.trim(),
        aspect_ratio: aspectRatio,
        episode_target: episodeTarget,
        project_type: projectType,
        default_locale: defaultLocale,
      })
      onClose()
      onSaved?.()
      toast.success(t('hub.editSaved'))
    } catch (e) {
      if (e instanceof AuthSessionExpiredError) return
      const resolved = resolveApiErrorMessage(e, 'hub.editFailed')
      const message = formatResolvedApiError(t, resolved)
      setError(message)
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ProtoModal
      open={open}
      onClose={() => {
        if (submitting) return
        onClose()
      }}
      title={t('hub.editTitle')}
      wide
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </ProtoBtn>
          <ProtoBtn size="sm" onClick={() => void handleSubmit()} disabled={submitting || !production}>
            {submitting ? t('common.processing') : t('common.save')}
          </ProtoBtn>
        </>
      }
    >
      {error ? (
        <p className="mb-4 rounded-xl border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          {error}
        </p>
      ) : null}
      <div className="space-y-3">
        <div>
          <ProtoLabel required>{t('createModal.fieldTitle')}</ProtoLabel>
          <ProtoInput value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div>
          <ProtoLabel>{t('createModal.fieldDescription')}</ProtoLabel>
          <ProtoTextarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <ProtoLabel required>{t('createModal.fieldType')}</ProtoLabel>
            <ProtoSelect value={projectType} onChange={(e) => setProjectType(e.target.value)}>
              {PROJECT_TYPES.map((k) => (
                <option key={k} value={k}>
                  {t(`production.projectType.${k}`, { defaultValue: k })}
                </option>
              ))}
            </ProtoSelect>
          </div>
          <div>
            <ProtoLabel required>{t('createModal.fieldAspect')}</ProtoLabel>
            <ProtoSelect value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)}>
              <option value="9:16">9:16</option>
              <option value="16:9">16:9</option>
              <option value="1:1">1:1</option>
              <option value="2.39:1">2.39:1</option>
            </ProtoSelect>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <ProtoLabel required>{t('createModal.fieldEpisodes')}</ProtoLabel>
            <ProtoNumberInput min={1} value={episodeTarget} onValueChange={setEpisodeTarget} />
          </div>
          <div>
            <ProtoLabel required>{t('createModal.fieldLocale')}</ProtoLabel>
            <ProtoSelect value={defaultLocale} onChange={(e) => setDefaultLocale(e.target.value)}>
              <option value="zh-CN">简体中文</option>
              <option value="en-US">English</option>
            </ProtoSelect>
          </div>
        </div>
        {production?.role ? (
          <p className="text-[11px] text-zinc-500">
            {t('hub.myRole', {
              role: t(`production.roles.${production.role as MemberRole}`, { defaultValue: production.role }),
            })}
          </p>
        ) : null}
      </div>
    </ProtoModal>
  )
}
